import { businessStore } from 'entities/index';
import React, { useEffect, useState } from 'react';
import { IEmployeeInfo } from 'shared/api/business/interfaces/employee-info.interface';
import { CircleImage } from 'shared/ui';
import styles from './step-employee.module.scss';
import { CheckMarkSvg } from 'shared/svg';
import { useParams } from 'react-router-dom';

interface IStepEmployee {
  selectedEmployeeId?: string;
  serviceId?: string;
  onSelect: (employeeId: string) => void;
  unavailableEmployeeIds?: string[];
}

export const StepEmployee: React.FC<IStepEmployee> = ({
  selectedEmployeeId,
  serviceId,
  onSelect,
  unavailableEmployeeIds = [],
}) => {
  const [employees, setEmployees] = useState<Partial<IEmployeeInfo>[]>([]);
  const params = useParams();
  const pointId = businessStore.pointId || params.pointId;

  useEffect(() => {
    if (!pointId) return;
    if (
      !businessStore.servicesList ||
      businessStore.servicesList.length === 0
    ) {
      businessStore.initServicesList(pointId);
    }
    if (
      !businessStore.employeesWidgetData ||
      businessStore.employeesWidgetData.length === 0
    ) {
      businessStore.initEmployeesWidgetData(pointId);
    }
  }, [pointId]);

  useEffect(() => {
    if (!businessStore.servicesList || businessStore.servicesList.length === 0)
      return;

    if (serviceId) {
      setEmployees(
        businessStore.servicesList.find((service) => service.id === serviceId)
          ?.employees || []
      );
    } else if (
      businessStore.employeesWidgetData &&
      businessStore.employeesWidgetData.length > 0
    ) {
      setEmployees(businessStore.employeesWidgetData);
    } else {
      const results = businessStore.servicesList
        .flatMap((service) => service.employees)
        .filter(
          (employee, index, self) =>
            index === self.findIndex((emp) => emp.id === employee.id)
        );
      setEmployees(results || []);
    }
  }, [
    businessStore.servicesList,
    serviceId,
    businessStore.employeesWidgetData,
  ]);

  const employeeCard = (employee: Partial<IEmployeeInfo>) => {
    const isSelected = selectedEmployeeId === employee.id;
    const isUnavailable = employee.id
      ? unavailableEmployeeIds.includes(employee.id)
      : false;
    return (
      <div
        className={`${styles.container__card} ${isSelected ? styles.container__card__selected : ''} ${isUnavailable ? styles.container__card__unavailable : ''}`}
        key={employee.id}
        onClick={() => {
          if (!isUnavailable) {
            onSelect(employee.id || '');
          }
        }}
      >
        <div className={styles.card__data}>
          <CircleImage imgSrc={employee.imgUrl} imgSize={32} />
          <div className={styles.info__name}>
            {employee.fio}
            {isUnavailable && (
              <span className={styles.unavailable_label}>не работает</span>
            )}
          </div>
        </div>
        <div
          className={`${styles.card__checkbox} ${isSelected ? styles.card__checkbox__selected : ''}`}
        >
          {isSelected && <CheckMarkSvg />}
        </div>
      </div>
    );
  };

  const anyEmployeeCard = () => {
    const isSelected = !selectedEmployeeId;
    return (
      <div
        className={`${styles.container__card} ${isSelected ? styles.container__card__selected : ''}`}
        key="any-employee"
        onClick={() => onSelect('')}
      >
        <div className={styles.card__data}>
          <div className={styles.container__card__any_icon}>
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="16" cy="10" r="4" fill="currentColor" />
              <path
                d="M8 26c0-4.418 3.582-8 8-8s8 3.582 8 8"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
            </svg>
          </div>
          <div className={styles.info__name}>
            Любой специалист
            <span className={styles.any_label}>подберётся автоматически</span>
          </div>
        </div>
        <div
          className={`${styles.card__checkbox} ${isSelected ? styles.card__checkbox__selected : ''}`}
        >
          {isSelected && <CheckMarkSvg />}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.selector__container}>
      {anyEmployeeCard()}
      {employees.map((employee) => employeeCard(employee))}
    </div>
  );
};
